import { useEffect, useRef, useState } from 'react'
import { Plus, CirclePause, CirclePlay, Trash2, Copy } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/custom/button'
import PopupConfirm from '@/components/custom/popup-confirm'
import DataTable from '@/components/custom/data-table'
import { useShallow } from 'zustand/shallow'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import useDebounce from '@/hooks/use-debounce'
import { useWorkflowStore } from '@/store/workflow-store'
import QueryKey from '@/constants/use-query-key'
import { throwException } from '@/exceptions/throw-exception'
import {
  getWorkflows,
  deleteWorkflow,
  copyWorkflow,
  changeStatusWorkflow,
} from '@/api/workflow'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { formatDayWithTime } from '@/utils/date'

export default function Workflow() {
  const firstMountRef = useRef(true)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchText, setSearchText] = useState('')
  const debounceSearchText = useDebounce(searchText, 500)
  const [
    page,
    pageSize,
    setPage,
    setPageSize,
    lastReloadTime,
    resetLastReloadTime,
  ] = useWorkflowStore(
    useShallow((state) => [
      state.page,
      state.pageSize,
      state.setPage,
      state.setPageSize,
      state.lastReloadTime,
      state.resetLastReloadTime,
    ]),
  )

  useEffect(() => {
    if (!firstMountRef.current) {
      resetLastReloadTime()
    }
  }, [debounceSearchText, resetLastReloadTime])

  const { isLoading, data: queryData } = useQuery({
    queryKey: [
      QueryKey.GET_LIST_WORKFLOW,
      {
        lastReloadTime,
        page,
        pageSize,
      },
    ],
    queryFn: async () => {
      const params = {
        page,
        page_size: pageSize,
      }

      if (searchText) {
        params.name = searchText.trim()
      }

      const res = await getWorkflows(params)

      firstMountRef.current = false

      return res?.data || {}
    },
  })

  const { isPending: isPendingDelete, mutate: handleDeleteWorkflow } =
    useMutation({
      mutationFn: (id) => deleteWorkflow(id),
      onSuccess: () => {
        toast.success('Xóa thành công')

        queryClient.invalidateQueries({
          queryKey: [QueryKey.GET_LIST_WORKFLOW],
        })
      },
      onError: throwException,
    })

  const { isPending: isPendingCopy, mutate: handleCopWorkflow } = useMutation({
    mutationFn: (id) => copyWorkflow(id),
    onSuccess: () => {
      toast.success('Sao chép thành công')

      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_LIST_WORKFLOW],
      })
    },
    onError: throwException,
  })

  const { isPending: isPendingUpdate, mutate: handleUpdateWorkflow } =
    useMutation({
      mutationFn: ({ id, payload }) => changeStatusWorkflow(id, payload),
      onSuccess: () => {
        toast.success('Cập nhật thành công')

        queryClient.invalidateQueries({
          queryKey: [QueryKey.GET_LIST_WORKFLOW],
        })
      },
      onError: throwException,
    })

  const workflows = isLoading ? [] : queryData?.data || []
  const total = isLoading ? 0 : queryData?.total || 0

  const columns = [
    {
      accessorKey: 'name',
      header: () => <div>Tên kịch bản</div>,
      cell: ({ row }) => {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <p className="max-w-[450px] truncate">{row.original.name}</p>
              </TooltipTrigger>
              <TooltipContent>{row.original.name}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      },
    },
    {
      accessorKey: 'event.code',
      header: () => <div className="text-center">Sự kiện</div>,
      className: 'justify-center',
      cell: ({ row }) => {
        const workflow = row.original

        return workflow.workflow_steps.trigger.target === 'tickets'
          ? 'Ticket'
          : 'Chat'
      },
    },
    {
      accessorKey: 'active',
      header: () => <div className="text-center">Trạng thái</div>,
      className: 'justify-center',
      cell: ({ row }) => {
        const workflow = row.original

        return (
          <div
            className={cn(
              'flex h-[28px] w-[120px] items-center justify-center truncate rounded-md bg-[#DFE5EB] text-[#09090B]',
              workflow.status === 'active' && 'bg-[#E3F9E9] text-[#00800C]',
              workflow.status === 'draft' && 'bg-[#F1F4FF] text-[#475467]',
            )}
          >
            <p>
              {workflow.status === 'draft'
                ? 'Bản nháp'
                : workflow.status === 'active'
                  ? 'Hoạt động'
                  : 'Không hoạt động'}
            </p>
          </div>
        )
      },
    },
    {
      accessorKey: 'created_by',
      header: () => <div className="text-center">Người khởi tạo</div>,
      className: 'justify-center',
      cell: ({ row }) => {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <p className="max-w-[250px] truncate">
                  {row.original.created_by}
                </p>
              </TooltipTrigger>
              <TooltipContent>{row.original.created_by}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      },
    },
    {
      accessorKey: 'created_at',
      header: () => <div className="text-center">Thời gian khởi tạo</div>,
      className: 'justify-center',
      cell: ({ row }) => {
        const workflow = row.original

        return <p>{formatDayWithTime(workflow.created_at)}</p>
      },
    },
    {
      accessorKey: 'updated_at',
      header: () => <div className="text-center">Thời gian cập nhật</div>,
      className: 'justify-center',
      cell: ({ row }) => {
        const workflow = row.original

        return <p>{formatDayWithTime(workflow.updated_at)}</p>
      },
    },
    {
      accessorKey: 'action',
      header: () => <div className="text-end">Hành động</div>,
      className: 'justify-end',
      cell: ({ row }) => {
        const workflow = row.original

        return (
          <div className="flex items-center gap-4">
            {workflow.status === 'inactive' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <CirclePlay
                      className={cn(
                        'h-4 w-4 cursor-pointer text-[#099516]',
                        isPendingUpdate && 'cursor-not-allowed',
                      )}
                      onClick={(e) => {
                        e.stopPropagation()

                        if (isPendingUpdate || workflow.status === 'draft')
                          return

                        handleUpdateWorkflow({
                          id: workflow._id,
                          payload: {
                            status: 'active',
                          },
                        })
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>Xuất bản</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {workflow.status === 'active' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <CirclePause
                      className={cn(
                        'h-4 w-4 cursor-pointer text-[#1F73B7]',
                        isPendingUpdate && 'cursor-not-allowed',
                      )}
                      onClick={(e) => {
                        e.stopPropagation()

                        if (isPendingUpdate || workflow.status === 'draft')
                          return

                        handleUpdateWorkflow({
                          id: workflow._id,
                          payload: {
                            status: 'inactive',
                          },
                        })
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>Không xuất bản</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Copy
                    className={cn(
                      'h-4 w-4 cursor-pointer',
                      (isPendingCopy || workflow.status === 'draft') &&
                        'cursor-not-allowed',
                    )}
                    onClick={(e) => {
                      e.stopPropagation()

                      if (isPendingCopy || workflow.status === 'draft') return

                      handleCopWorkflow(workflow._id)
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>Sao chép</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <PopupConfirm
              showTooltip
              trigger={
                <Trash2
                  className={cn(
                    'h-4 w-4 cursor-pointer text-[hsl(var(--icon-danger))]',
                    isPendingDelete && 'cursor-not-allowed',
                  )}
                />
              }
              title={'Xóa'}
              description={`Bạn có chắc chắn muốn xóa  ${workflow.name} không?`}
              onConfirm={() => handleDeleteWorkflow(workflow._id)}
            />
          </div>
        )
      },
    },
  ]

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <Card className="h-full overflow-hidden">
        <CardContent className="flex h-full flex-col gap-2 p-4">
          <div className="sticky top-0 flex flex-col gap-2">
            <h1 className="text-lg font-bold">Danh sách tự động hóa</h1>
            <div className="flex items-center justify-between">
              <div className="w-[40%]">
                <Input
                  type="text"
                  placeholder="Tìm kiếm theo tên kịch bản"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              <Button
                leftSection={<Plus className="h-4 w-4" />}
                onClick={() => {
                  navigate({
                    to: '/automation/editor',
                  })
                }}
              >
                Tạo tự động hóa
              </Button>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={workflows}
            isLoading={isLoading}
            total={total}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            handleRowClick={(row) => {
              navigate({
                to: `/automation/editor/${row.original._id}`,
              })
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}
